/**
 * ReportIssueButton — floating "🐛 Zgłoś problem" (prawy-dolny róg, globalny).
 *
 * V2 2026-05-18. Wzorzec z LES-LEX-PL, adaptacja do MASKIL routingu.
 * Modal: tytuł / opis / kategoria / priorytet. Auto-context: URL, moduł, ekran.
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Bug, Loader2, CheckCircle2, Paperclip, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { submitUserReport, attachToUserReport } from '../../lib/api';
import { getDiagnostics } from '../../lib/diagnostics';

const ATTACH_MAX_COUNT = 3;
const ATTACH_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const ATTACH_MIME_OK = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];

function detectModule(p) {
  if (p.includes('/investments')) return 'investments';
  if (p.includes('/tenders')) return 'tenders';
  if (p.includes('/pre-tender')) return 'pretender';
  if (p.includes('/automations')) return 'automations';
  if (p.includes('/my-dashboard')) return 'dashboard_widgets';
  if (p.includes('/architects')) return 'architects';
  if (p.includes('/suppliers')) return 'suppliers';
  if (p.includes('/contractors')) return 'contractors';
  if (p.includes('/products')) return 'products';
  if (p.includes('/inbox')) return 'inbox';
  if (p.includes('/emails')) return 'emails';
  if (p.includes('/proposals') || p.includes('/offers')) return 'offers';
  if (p.includes('/intelligence')) return 'intelligence';
  if (p.includes('/negotiations')) return 'negotiations';
  if (p.includes('/feed')) return 'feed';
  if (p.includes('/plans')) return 'plans';
  if (p.includes('/admin')) return 'admin';
  if (p.includes('/settings')) return 'settings';
  if (p.includes('/zgloszenia')) return 'reports';
  if (p === '/' || p.includes('/home') || p.includes('/dashboard')) return 'dashboard';
  return 'other';
}

export default function ReportIssueButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'bug', severity: 'medium',
  });
  const [files, setFiles] = useState([]); // wybrane do uploadu (max 3, max 2MB każdy)

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
    setForm({ title: '', description: '', category: 'bug', severity: 'medium' });
    setFiles([]);
  };

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of picked) {
      if (files.length + accepted.length >= ATTACH_MAX_COUNT) {
        toast.error(`Maksymalnie ${ATTACH_MAX_COUNT} plików`);
        break;
      }
      if (f.size > ATTACH_MAX_BYTES) {
        toast.error(`„${f.name}" jest za duży (max 2 MB)`);
        continue;
      }
      if (!ATTACH_MIME_OK.includes(f.type)) {
        toast.error(`„${f.name}" — dozwolone tylko PNG/JPG/WEBP/PDF`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    e.target.value = ''; // reset input żeby same-file mógł być wybrany ponownie
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (form.title.trim().length < 3) { toast.error('Tytuł min. 3 znaki'); return; }
    if (form.description.trim().length < 10) { toast.error('Opis min. 10 znaków'); return; }
    setSubmitting(true);
    try {
      const diag = getDiagnostics();
      const res = await submitUserReport({
        ...form,
        module: detectModule(location.pathname),
        context: {
          url: window.location.href,
          pathname: location.pathname,
          screen: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          user_agent: navigator.userAgent,
          timestamp_local: new Date().toISOString(),
          js_errors: diag.js_errors,
          failed_requests: diag.failed_requests,
        },
      });

      // Po utworzeniu raportu — upload załączników (jeśli wybrane)
      const reportId = res?.report_id || res?.id;
      if (reportId && files.length > 0) {
        let attached = 0;
        for (const f of files) {
          try {
            await attachToUserReport(reportId, f);
            attached += 1;
          } catch (err) {
            toast.error(`Nie udało się dołączyć „${f.name}"`);
          }
        }
        if (attached > 0) toast.info(`Dołączono ${attached}/${files.length} plików`);
      }

      setSubmitted(true);
      toast.success(res?.message || 'Zgłoszenie przyjęte');
      setTimeout(() => setOpen(false), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Błąd podczas wysyłki zgłoszenia');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
        title="Zgłoś problem lub propozycję"
        aria-label="Zgłoś problem"
        data-testid="report-issue-btn"
      >
        <Bug className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-primary" /> Zgłoś problem
            </DialogTitle>
            <DialogDescription>
              Opisz krótko co nie działa albo co chciałbyś dodać. Zespół zajmie się zgłoszeniem.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-8 flex flex-col items-center gap-3 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="text-lg font-medium">Zgłoszenie przyjęte!</p>
              <Button variant="outline" size="sm" onClick={() => { setOpen(false); navigate('/zgloszenia'); }}>
                Zobacz „Moje zgłoszenia”
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Tytuł *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Np. Kanban nie zapisuje zmiany etapu"
                  maxLength={200}
                  data-testid="report-title"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Opis *</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Co próbowałeś zrobić? Co się stało? Jaki błąd widzisz?"
                  rows={5}
                  maxLength={5000}
                  data-testid="report-desc"
                />
                <p className="text-xs text-muted-foreground mt-1">{form.description.length}/5000</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Kategoria</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Błąd</SelectItem>
                      <SelectItem value="feature_request">Propozycja funkcji</SelectItem>
                      <SelectItem value="performance">Wydajność</SelectItem>
                      <SelectItem value="ui_ux">Interfejs / UX</SelectItem>
                      <SelectItem value="data_issue">Problem z danymi</SelectItem>
                      <SelectItem value="login_issue">Logowanie</SelectItem>
                      <SelectItem value="payment">Płatności</SelectItem>
                      <SelectItem value="other">Inne</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Priorytet</label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niski</SelectItem>
                      <SelectItem value="medium">Średni</SelectItem>
                      <SelectItem value="high">Wysoki</SelectItem>
                      <SelectItem value="critical">Krytyczny</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Załączniki (V2.1 — 2026-05-26) */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Załączniki <span className="text-muted-foreground font-normal">(opcjonalnie — max 3, max 2 MB każdy: PNG/JPG/WEBP/PDF)</span>
                </label>
                <div className="space-y-1.5">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-muted/30 rounded px-2 py-1 text-xs">
                      <Paperclip className="h-3 w-3 text-muted-foreground" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="text-muted-foreground hover:text-red-300"
                        title="Usuń"
                      >
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {files.length < ATTACH_MAX_COUNT && (
                    <label className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer">
                      <Paperclip className="h-3.5 w-3.5" />
                      Dodaj plik
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,application/pdf"
                        multiple
                        onChange={handleFilePick}
                        className="hidden"
                        data-testid="report-file-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                Automatycznie dołączamy: aktualną stronę ({detectModule(location.pathname)}),
                pełny URL, przeglądarkę/OS, rozdzielczość oraz ostatnie błędy techniczne
                (konsola JS + nieudane zapytania) — by zespół szybciej znalazł problem.
                Nie zbieramy haseł ani danych z projektów (sekrety są maskowane).
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                  Anuluj
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="report-submit">
                  {submitting
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Wysyłanie...</>
                    : 'Wyślij zgłoszenie'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
